import React, { useEffect, useRef, useState } from 'react';
import { resolveAssetKey } from '../utils/trackImage';
import { getPresignedUrl } from '../utils/presignedUrlCache';

const PresignedImage = ({
  assetKey,
  assetType = 'track_image',
  status,
  resolved = false,
  lazy = true,
  alt = '',
  className,
}) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!lazy || isVisible) {
      return undefined;
    }

    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [lazy, isVisible]);

  useEffect(() => {
    let cancelled = false;

    if (!isVisible || !assetKey) {
      if (!assetKey) {
        setSrc(null);
      }
      return undefined;
    }

    const key = resolved
      ? assetKey
      : resolveAssetKey(assetKey, assetType, status);
    if (!key) {
      setSrc(null);
      return undefined;
    }

    if (key.startsWith('http') || key.startsWith('/')) {
      setSrc(key);
      return undefined;
    }

    (async () => {
      try {
        const presignedUrl = await getPresignedUrl(key, { status, assetType });
        if (!cancelled) {
          setSrc(presignedUrl || null);
        }
      } catch (error) {
        console.error('Failed to fetch signed asset URL:', error);
        if (!cancelled) {
          setSrc(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isVisible, assetKey, assetType, status, resolved]);

  return (
    <span
      ref={containerRef}
      className="presigned-image-slot"
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className={className}
          loading="lazy"
          decoding="async"
          onError={(event) => {
            event.target.onerror = null;
            event.target.style.display = 'none';
          }}
        />
      ) : null}
    </span>
  );
};

export default PresignedImage;
